import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { commissionsApi } from "@/api/commissions";
import type { UpdateAgentCommissionPayload } from "@/types/commission";

// ============================
// Query Key Factory
// ============================

export const commissionKeys = {
  all: ["commissions"] as const,
  config: () => [...commissionKeys.all, "config"] as const,
};

// ============================
// Queries
// ============================

export function commissionConfigQueryOptions() {
  return queryOptions({
    queryKey: commissionKeys.config(),
    queryFn: () => commissionsApi.getConfig(),
    staleTime: 30 * 1000,
  });
}

export function useCommissionConfig() {
  return useQuery(commissionConfigQueryOptions());
}

// ============================
// Mutations
// ============================

export function useUpdateDefaultCommission() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (defaultRate: number) =>
      commissionsApi.updateDefault({ defaultRate }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: commissionKeys.config() });
    },
  });
}

export function useUpdateAgentCommission() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      agentId,
      payload,
    }: {
      agentId: string;
      payload: UpdateAgentCommissionPayload;
    }) => commissionsApi.updateAgent(agentId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: commissionKeys.config() });
    },
  });
}

export function useResetAllCommissions() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => commissionsApi.resetAll(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: commissionKeys.config() });
    },
  });
}
